import type { PaletteColorOptions, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import type React from 'react';
import type {} from '@mui/x-date-pickers/themeAugmentation';

// Update the Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}
declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    tertiary: true;
  }
}

type Warnings = {
  minor: string;
  moderate: string;
  major: string;
};

type Graph = {
  colors: {
    primaryLine: string;
    primaryFill: string;
    secondaryLine: string;
    feedLine: string;
    background: string;
  };
};

export type BoxSizes = 'small' | 'medium' | 'large' | 'xlarge' | 'full';

type BoxSize = Record<BoxSizes, string>;

declare module '@mui/material/styles' {
  interface Theme {
    graph: Graph;
    boxSize: BoxSize;
  }
  interface Palette {
    warnings: Warnings;
    tertiary: Palette['primary'];
    white: string;
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    graph: Graph;
    boxSize: BoxSize;
  }
  interface PaletteOptions {
    white?: string;
    offWhite?: string;
    primary?: PaletteColorOptions;
    secondary?: PaletteColorOptions;
    tertiary?: PaletteColorOptions;
    success?: PaletteColorOptions;
    info?: PaletteColorOptions;
    error?: PaletteColorOptions;
    warning?: PaletteColorOptions;
    warnings?: Warnings;
  }

  interface TypographyVariants {
    alarmBadge: React.CSSProperties;
    tableHeader?: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    alarmBadge?: React.CSSProperties;
    tableHeader?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    alarmBadge: true;
    tableHeader: true;
  }
}
const graph = {
  colors: {
    primaryLine: '#0069AB',
    primaryFill: '#EBF7FF80',
    secondaryLine: '#BDBDBD',
    feedLine: '#FFB801',
    background: 'transparent',
  },
};

const boxSize = {
  small: '93px',
  medium: '148px',
  large: '202px',
  xlarge: '272px',
  full: '312px',
};

const palette = {
  white: '#FFFFFF',
  primary: {
    main: '#A08C64',
    dark: '#897858',
  },
  secondary: {
    main: '#FF824B',
  },
  tertiary: {
    main: '#EEEEEE',
  },
  success: {
    main: '#129B4E',
    light: '#E1FAEC',
  },
  info: {
    main: '#327DFF',
    light: '#C8DCFF',
  },
  error: {
    main: '#FF2E2E',
    light: '#FAE1E1',
  },
  grey: {
    '50': '#FAFAFA',
    '100': '#F5F5F5',
    '200': '#EEEEEE',
    '300': '#E0E0E0',
    '400': '#BDBDBD',
    '500': '#9E9E9E',
    '600': '#757575',
    '700': '#616161',
    '800': '#424242',
    '900': '#212121',
  },
  warnings: {
    minor: '#FFAE57',
    moderate: '#FF824B',
    major: '#FF2E2E',
  },
};

const components: ThemeOptions['components'] = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 700,
        boxShadow: 'none',
        borderRadius: '50px',
        borderColor: palette.grey[300],
        '&.MuiButton-outlined.MuiButton-colorTertiary': {
          color: palette.grey[900],
        },
        '&.MuiButton-outlined.MuiButton-colorTertiary:hover': {
          borderColor: palette.grey[500],
        },
        '&.MuiButton-contained.MuiButton-colorTertiary:hover': {
          outline: `1px solid ${palette.grey[500]}`,
        },
        '&.MuiButton-text.MuiButton-colorTertiary': {
          color: palette.grey[900],
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        borderRadius: '8px',
        caretColor: 'black',
        background: palette.grey['100'],
        '&:hover': {
          background: palette.grey['200'],
        },
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
        '&.Mui-disabled': {
          background: `${palette.grey['100']} !important`,
        },
        '&.MuiInputBase-colorTertiary': {
          background: 'white',
          '> fieldset': {
            border: `1px solid ${palette.grey[400]}`,
          },
          '&:hover': {
            '> fieldset': {
              border: `1px solid ${palette.grey[600]}`,
            },
          },
          '&:focus-within': {
            '> fieldset': {
              border: `1px solid ${palette.grey[600]}`,
            },
          },
        },
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        background: palette.grey['100'],
        '&.Mui-disabled': {
          background: `${palette.grey['100']} !important`,
        },
      },
    },
  },
  // Date fields render their own input instead of InputBase/FilledInput,
  // so mirror the overrides above to keep them consistent with other inputs.
  MuiPickersInputBase: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        caretColor: 'black',
      },
    },
  },
  MuiPickersFilledInput: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
        background: palette.grey['100'],
        '&:hover': {
          background: palette.grey['200'],
        },
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
        '&.Mui-disabled': {
          background: `${palette.grey['100']} !important`,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '10px',
        transform: 'none',
        left: '12px',
        top: '6px',
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        letterSpacing: '0.2px',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        letterSpacing: '0.2px',
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        '&.MuiSkeleton-rectangular': {
          borderRadius: '4px',
        },
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        zIndex: 1300,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: 12,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        color: palette.grey[900],
      },
    },
  },
};

const typography = {
  fontFamily: 'Sandvik Sans Text',
  fontWeight: 500,
  body1: {
    fontWeight: 500,
  },
  body2: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '140%',
    letterSpacing: '0.17px',
  },
  overline: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '31.92px',
    letterSpacing: '1px',
  },
  alarmBadge: {
    fontWeight: 700,
    fontSize: 10,
  },
  tableHeader: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '24px',
    letterSpacing: '0.17px',
  },
};

const theme = createTheme({
  graph,
  boxSize,
  palette,
  components,
  typography,
});

export default theme;
